import React, { useState, createRef, useEffect } from 'react';
import styles from './MultiSelectionInput.module.sass';
import Icon from '../../Icon/Icon';
import { useGlobalValue } from '../../../state/GlobalProvider';

const MultiSelectionInput = props => {
  const {
    name,
    className,
    options,
    value,
    placeholder,
    onChange
  } = props;
  const {
    i18n: globalI18n
  } = useGlobalValue();
  const i18n = globalI18n;
  const [filter, setFilter] = useState('');
  const [focus, setFocus] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const input = /*#__PURE__*/createRef(null);
  const optionsElem = /*#__PURE__*/createRef(null);
  const container = /*#__PURE__*/createRef(null);
  let timeout = null;
  useEffect(() => {
    const handleClick = e => {
      if (optionsElem.current) {
        const containerRect = container.current.getBoundingClientRect();
        const rect = optionsElem.current.getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;
        const insideInput = x >= containerRect.left && x <= containerRect.left + containerRect.width && y >= containerRect.top && y <= containerRect.top + containerRect.height;
        const insideOptions = x >= rect.left && x <= rect.left + rect.width && y >= rect.top && y <= rect.top + rect.height;

        if (insideInput || insideOptions) {
          e.preventDefault();
          e.stopPropagation();
          optionsElem.current.keepFocus = true;
          input.current.focus();
          setFocus(true);
        } else {
          setFocus(false);
        }
      }
    };

    const doc = input.current.ownerDocument || window.document;
    const w = input.current.ownerDocument.defaultView || input.current.ownerDocument.parentView || window;
    doc.addEventListener('mousedown', handleClick);
    doc.addEventListener('touchstart', handleClick);
    return () => {
      doc.removeEventListener('mousedown', handleClick);
      doc.removeEventListener('touchstart', handleClick);
    };
  }, [input, optionsElem, container]);
  const selected = (value || []).map(id => {
    const option = options.find(({
      id: optionId
    }) => id === optionId);
    const isHighlighted = id === toDelete;
    return option ? /*#__PURE__*/React.createElement("span", {
      key: option.id,
      className: `${styles['selected-item']} ${isHighlighted ? styles['highlighted'] : ''}`
    }, /*#__PURE__*/React.createElement("span", {
      className: styles['option-label']
    }, i18n`${option.name}`), /*#__PURE__*/React.createElement("span", {
      className: styles['remove-button'],
      onClick: e => {
        e.preventDefault();
        e.stopPropagation();
        const valueIndex = (value || []).findIndex(vId => vId === id);
        const v = [...value];
        v.splice(valueIndex, 1);
        onChange && onChange({
          target: {
            value: v
          }
        });
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      name: "remove"
    }))) : null;
  }).filter(opt => opt);
  const filteredItems = (options || []).filter(({
    name
  }) => {
    const filterString = (filter || '').split(' ').join('.*');
    return (name || '').match(new RegExp(`.*${filterString}.*`, 'ig'));
  });
  const optionList = filteredItems.map(({
    id,
    name
  }) => {
    const isSelected = (value || []).find(valueId => id === valueId);
    return /*#__PURE__*/React.createElement("div", {
      key: id,
      className: styles['option'],
      onClickCapture: e => {
        e.preventDefault();
        e.stopPropagation();
        const valueIndex = (value || []).findIndex(vId => vId === id);
        let nextValue;

        if (valueIndex < 0) {
          nextValue = [...(value || []), id];
        } else {
          const v = [...value];
          v.splice(valueIndex, 1);
          nextValue = v;
        }

        onChange && onChange({
          target: {
            value: nextValue
          }
        });
      }
    }, /*#__PURE__*/React.createElement("small", {
      className: `${styles['checkmark']} ${isSelected ? styles['is-selected'] : ''}`
    }, isSelected && /*#__PURE__*/React.createElement(Icon, {
      name: "checkmark"
    })), /*#__PURE__*/React.createElement("small", null, i18n`${name}`));
  });
  return /*#__PURE__*/React.createElement("label", {
    ref: container,
    className: `${styles['container']} ${focus ? styles['focus-within'] : ''} ${className || ''}`,
    onBlur: e => {
      if (!(optionsElem.current && optionsElem.current.keepFocus)) {
        timeout = setTimeout(() => {
          if (input.current) {
            setFocus(false);
            setToDelete(null);
          }
        }, 10);
      }

      if (optionsElem.current) {
        optionsElem.current.keepFocus = false;
      }
    }
  }, /*#__PURE__*/React.createElement("h5", {
    className: styles['label']
  }, name), /*#__PURE__*/React.createElement("div", {
    className: styles['input']
  }, selected, /*#__PURE__*/React.createElement("input", {
    ref: input,
    placeholder: placeholder,
    value: filter,
    onFocus: () => {
      clearTimeout(timeout);
      setFocus(true);
    },
    onKeyDown: e => {
      switch (e.key.toLowerCase()) {
        case 'backspace':
          {
            if (!filter.length) {
              if (!toDelete) {
                setToDelete((value || [])[(value || []).length - 1]);
              } else {
                const nextValue = [...(value || [])];
                nextValue.pop();
                onChange && onChange({
                  target: {
                    value: nextValue
                  }
                });
                setToDelete(null);
              }
            }

            break;
          }

        case 'enter':
          {
            const id = (filteredItems[0] || {}).id;
            const idIndex = (value || []).findIndex(vId => vId === id);

            if (idIndex < 0 && id && onChange) {
              onChange && onChange({
                target: {
                  value: [...(value || []), id]
                }
              });
            }

            break;
          }

        default:
          {
            setToDelete(null);
            break;
          }
      }
    },
    onChange: e => {
      setFilter(e.target.value);
    }
  })), /*#__PURE__*/React.createElement("div", {
    ref: optionsElem,
    className: styles['options']
  }, optionList));
};

export default MultiSelectionInput;